// Regroups one parsed 850, 852, 855, 856, 997 or 840 transaction by *kind
// of data* (order/report details, dates, parties, terms, line/activity
// items, notes, totals) rather than by X12 loop position, so the UI can
// show a short list of collapsible sections a reader recognizes. Each
// rendered segment keeps the exact finding-path base the validator uses
// (lib/validator.ts), so findings can be rolled up onto any collapsed
// section, party or line before the user expands it. The 856's Shipments
// panel is built the same way but is structurally different (a recursive
// HL tree, not a flat line-item list), so that logic lives in its own
// module, ./sections856.ts; buildSections below dispatches to whichever of
// the transaction set types is parsed.

import type {EdiSegment} from './tokenizer';
import type {
  Header850,
  ItemLoop850,
  ItemNameSubLoop850,
  NameLoop850,
  Parsed850,
} from './parser850';
import type {
  Header852,
  ItemActivityLoop852,
  ItemNameSubLoop852,
  NameLoop852,
  Parsed852,
} from './parser852';
import type {
  Header855,
  ItemAckLoop855,
  ItemNameSubLoop855,
  NameLoop855,
  Parsed855,
} from './parser855';
import type {Parsed997} from './parser997';
import type {
  Header840,
  ItemLoop840,
  ItemNameSubLoop840,
  NameLoop840,
  Parsed840,
} from './parser840';
import type {Header810, ItemLoop810, NameLoop810, Parsed810} from './parser810';
import type {
  Header843,
  ItemLoop843,
  ItemNameSubLoop843,
  NameLoop843,
  Parsed843,
} from './parser843';
import type {Header844, ItemLoop844, NameLoop844, Parsed844} from './parser844';
import type {EdiTransactionExplained} from './explainedEnvelope';
import type {ValidationFinding} from './validator';
import {transactionPathPrefix} from './segmentPaths';
import {buildSections856} from './sections856';
import {buildSections845} from './sections845';
import {buildSections867} from './sections867';
import {buildSections210} from './sections210';

export interface SectionEntry {
  segment: EdiSegment;
  /** Finding-path base, e.g. "groups[0].transactions[0].header.refs[1]". */
  basePath: string;
}

/** One collapsible unit inside a section — a single party (N1 loop) or a
 * single line (PO1/LIN). Sections that don't repeat use one untitled group.
 * `subgroups` is the one extra nesting level a 997 AK2 row's AK3 notes use
 * (each holding its AK3 segment plus its nested AK4 notes); 850/852 never
 * set it. */
export interface SectionGroup {
  key: string;
  entries: SectionEntry[];
  /** Nested children: HL children (Order under Shipment, Pack/Tare/Item under
   * Order) for the 856's Shipments panel, and AK3/AK4 notes for the 997 — absent
   * for a leaf group. */
  subgroups?: SectionGroup[];
}

export type SectionKey =
  | 'order'
  | 'dates'
  | 'parties'
  | 'terms'
  | 'items'
  | 'notes'
  | 'totals'
  | 'shipmentDetails'
  | 'shipments'
  | 'acknowledgmentSummary'
  | 'transactionSetAcks'
  | 'reportDetails'
  | 'lines'
  | 'stops';

/** The subset of SectionKey the 850/852/855 flat, non-nested layout uses —
 * excludes the 856- and 997-only tree-shaped keys. */
type CommonSectionKey = Exclude<
  SectionKey,
  | 'shipmentDetails'
  | 'shipments'
  | 'acknowledgmentSummary'
  | 'transactionSetAcks'
  | 'reportDetails'
  | 'lines'
  | 'stops'
>;

export interface Section {
  key: SectionKey;
  title: string;
  description: string;
  /** Whether each group is its own collapsible row (parties, line items). */
  repeating: boolean;
  groups: SectionGroup[];
}

const SECTION_ORDER: CommonSectionKey[] = [
  'order',
  'dates',
  'parties',
  'terms',
  'items',
  'notes',
  'totals',
];

/** Flattens a record of optional/repeated segments into pathed entries,
 * preserving the order keys are listed in. */
export function entriesOf<T extends object>(
  source: T,
  keys: ReadonlyArray<keyof T & string>,
  basePath: string
): SectionEntry[] {
  const out: SectionEntry[] = [];
  for (const key of keys) {
    const value = source[key] as EdiSegment | EdiSegment[] | undefined;
    if (Array.isArray(value)) {
      value.forEach((segment, i) =>
        out.push({segment, basePath: `${basePath}.${key}[${i}]`})
      );
    } else if (value) {
      out.push({segment: value, basePath: `${basePath}.${key}`});
    }
  }
  return out;
}

export interface IssueCounts {
  errors: number;
  warnings: number;
}

/** Counts the findings that fall on or under any of the given segment base
 * paths — used to highlight a collapsed row before it's expanded. */
export function countIssues(
  findings: ValidationFinding[],
  basePaths: string[]
): IssueCounts {
  const counts: IssueCounts = {errors: 0, warnings: 0};
  for (const finding of findings) {
    const matches = basePaths.some(
      p => finding.path === p || finding.path.startsWith(`${p}.`)
    );
    if (!matches) continue;
    if (finding.severity === 'error') counts.errors++;
    else counts.warnings++;
  }
  return counts;
}

export function groupBasePaths(groups: SectionGroup[]): string[] {
  return groups.flatMap(g => [
    ...g.entries.map(e => e.basePath),
    ...(g.subgroups ? groupBasePaths(g.subgroups) : []),
  ]);
}

// --- 850 ---------------------------------------------------------------

type HeaderKey850 = keyof Header850;

/** Which data-type section each 850 header-level segment belongs to. */
const HEADER_SECTION_850: Record<HeaderKey850, CommonSectionKey> = {
  beg: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  pwk: 'order',
  lm: 'order',
  lq: 'order',
  dtm: 'dates',
  ldt: 'dates',
  tax: 'terms',
  fob: 'terms',
  ctp: 'terms',
  itd: 'terms',
  dis: 'terms',
  inc: 'terms',
  pct: 'terms',
  iss: 'terms',
  msg: 'notes',
};

const SECTION_META_850: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Order details',
    description:
      'PO number, purpose, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Requested, promised and other order dates.',
  },
  parties: {
    title: 'Parties & addresses',
    description:
      'Who is involved — ship to, bill to, buyer, vendor — and where.',
  },
  terms: {
    title: 'Pricing, terms & shipping',
    description: 'Payment terms, tax, pricing, FOB and shipping instructions.',
  },
  items: {
    title: 'Line items',
    description: 'Each ordered product, quantity and price.',
  },
  notes: {title: 'Notes', description: 'Free-form messages on the order.'},
  totals: {title: 'Totals', description: 'Line-item count and order totals.'},
};

const NAME_LOOP_KEYS_850: ReadonlyArray<keyof NameLoop850> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
];
const ITEM_KEYS_850: ReadonlyArray<
  Exclude<keyof ItemLoop850, 'sln' | 'transportation' | 'subNameLoops'>
> = [
  'po1',
  'pid',
  'po3',
  'ctp',
  'mea',
  'pwk',
  'pkg',
  'refs',
  'per',
  'sac',
  'it8',
  'ctb',
  'tax',
  'fob',
  'sdq',
  'dtm',
  'ldt',
  'iss',
  'pam',
  'msg',
  'schedule',
  'csh',
  'tc2',
  'pct',
];
const ITEM_NAME_KEYS_850: ReadonlyArray<keyof ItemNameSubLoop850> = [
  'n1',
  'n2',
  'n3',
  'n4',
];

/** Builds the data-type sections for one 850 transaction, omitting any
 * section with nothing in it. */
function buildSections850(parsed: Parsed850, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_850) as HeaderKey850[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_850[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_850, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_850, path);
    if (item.sln)
      entries.push({segment: item.sln.sln, basePath: `${path}.sln.sln`});
    if (item.transportation) {
      entries.push({
        segment: item.transportation.tc1,
        basePath: `${path}.transportation.tc1`,
      });
    }
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_850, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_850[key], repeating, groups}];
  });
}

// --- 852 ---------------------------------------------------------------

type HeaderKey852 = keyof Header852;

/** Which data-type section each 852 header-level segment belongs to. */
const HEADER_SECTION_852: Record<HeaderKey852, CommonSectionKey> = {
  bct: 'order',
  refs: 'order',
  per: 'order',
  pwk: 'order',
  dtm: 'dates',
  fob: 'terms',
  msg: 'notes',
};

const SECTION_META_852: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Report details',
    description: 'Report reference number, activity type and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'The report period and other reported dates.',
  },
  parties: {
    title: 'Parties & locations',
    description:
      'Who this report is about — the reporting retailer/distributor — and where.',
  },
  terms: {
    title: 'Shipping',
    description: 'F.O.B. and shipping-related instructions.',
  },
  items: {
    title: 'Activity lines',
    description:
      'Each reported item and its quantities — units sold, on hand, on order, received.',
  },
  notes: {title: 'Notes', description: 'Free-form messages on the report.'},
  totals: {
    title: 'Totals',
    description: 'Line-activity count and report totals.',
  },
};

const NAME_LOOP_KEYS_852: ReadonlyArray<keyof NameLoop852> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
];
const ITEM_KEYS_852: ReadonlyArray<
  Exclude<keyof ItemActivityLoop852, 'subNameLoops'>
> = ['lin', 'pid', 'ctp', 'sdq', 'qty', 'refs', 'per', 'dtm'];
const ITEM_NAME_KEYS_852: ReadonlyArray<keyof ItemNameSubLoop852> = [
  'n1',
  'n2',
  'n3',
  'n4',
];

/** Builds the data-type sections for one 852 transaction, omitting any
 * section with nothing in it. */
function buildSections852(parsed: Parsed852, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_852) as HeaderKey852[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_852[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_852, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_852, path);
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_852, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_852[key], repeating, groups}];
  });
}

// --- 855 ---------------------------------------------------------------

type HeaderKey855 = keyof Header855;

/** Which data-type section each 855 header-level segment belongs to. */
const HEADER_SECTION_855: Record<HeaderKey855, CommonSectionKey> = {
  bak: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  fob: 'terms',
  itd: 'terms',
  td5: 'terms',
  dtm: 'dates',
  other: 'order',
};

const SECTION_META_855: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Acknowledgment details',
    description:
      'Acknowledgment type, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Dates referenced on the acknowledgment.',
  },
  parties: {
    title: 'Parties & addresses',
    description:
      'Who is involved — ship to, bill to, buyer, vendor — and where.',
  },
  terms: {
    title: 'Terms & shipping',
    description: 'Payment terms, FOB and shipping instructions.',
  },
  items: {
    title: 'Line item acknowledgments',
    description: 'Each acknowledged line, its status, quantity and detail.',
  },
  notes: {
    title: 'Notes',
    description: 'Free-form messages on the acknowledgment.',
  },
  totals: {
    title: 'Totals',
    description: 'Line-item count and acknowledgment totals.',
  },
};

const NAME_LOOP_KEYS_855: ReadonlyArray<keyof NameLoop855> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];
const ITEM_KEYS_855: ReadonlyArray<
  Exclude<keyof ItemAckLoop855, 'nameSubLoops'>
> = [
  'po1',
  'ack',
  'pid',
  'mea',
  'pkg',
  'po3',
  'ctp',
  'refs',
  'per',
  'sac',
  'sdq',
  'sch',
  'dtm',
  'other',
];
const ITEM_NAME_KEYS_855: ReadonlyArray<keyof ItemNameSubLoop855> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'other',
];

/** Builds the data-type sections for one 855 transaction, omitting any
 * section with nothing in it. */
function buildSections855(parsed: Parsed855, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_855) as HeaderKey855[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_855[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_855, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_855, path);
    item.nameSubLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.nameSubLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_855, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_855[key], repeating, groups}];
  });
}

// --- 840 -----------------------------------------------------------------

type HeaderKey840 = keyof Header840;

/** Which data-type section each 840 header-level segment belongs to. */
const HEADER_SECTION_840: Record<HeaderKey840, CommonSectionKey> = {
  bqt: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  dtm: 'dates',
  tax: 'terms',
  fob: 'terms',
  itd: 'terms',
  msg: 'notes',
  other: 'order',
};

const SECTION_META_840: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Quote request details',
    description:
      'RFQ number, purpose, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Required-by and other quote request dates.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved — buyer, vendor, ship from/to — and where.',
  },
  terms: {
    title: 'Pricing, terms & shipping',
    description: 'Payment terms, tax, FOB and shipping instructions.',
  },
  items: {
    title: 'Requested line items',
    description: 'Each requested product, quantity and requested price.',
  },
  notes: {
    title: 'Notes',
    description: 'Free-form messages on the quote request.',
  },
  totals: {title: 'Totals', description: 'Line-item count and quote totals.'},
};

const NAME_LOOP_KEYS_840: ReadonlyArray<keyof NameLoop840> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];
const ITEM_KEYS_840: ReadonlyArray<Exclude<keyof ItemLoop840, 'subNameLoops'>> =
  ['po1', 'pid', 'mea', 'ctp', 'sac', 'refs', 'per', 'sdq', 'dtm', 'other'];
const ITEM_NAME_KEYS_840: ReadonlyArray<keyof ItemNameSubLoop840> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'other',
];

/** Builds the data-type sections for one 840 transaction, omitting any
 * section with nothing in it. */
function buildSections840(parsed: Parsed840, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_840) as HeaderKey840[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_840[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_840, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_840, path);
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_840, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_840[key], repeating, groups}];
  });
}

// --- 810 -----------------------------------------------------------------

type HeaderKey810 = keyof Header810;

/** Which data-type section each 810 header-level segment belongs to. */
const HEADER_SECTION_810: Record<HeaderKey810, CommonSectionKey> = {
  big: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  pwk: 'order',
  dtm: 'dates',
  itd: 'terms',
  fob: 'terms',
  cad: 'terms',
  msg: 'notes',
  n9: 'notes',
  other: 'order',
};

const SECTION_META_810: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Invoice details',
    description:
      'Invoice number, purpose, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Invoice and other header dates.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved — bill-to, remit-to, ship to — and where.',
  },
  terms: {
    title: 'Terms & shipping',
    description: 'Payment terms, FOB and carrier/routing instructions.',
  },
  items: {
    title: 'Line items',
    description: 'Each invoiced product, quantity and price.',
  },
  notes: {
    title: 'Notes',
    description: 'Free-form messages on the invoice.',
  },
  totals: {
    title: 'Totals',
    description: 'Invoice total, allowances/charges, tax and line count.',
  },
};

const NAME_LOOP_KEYS_810: ReadonlyArray<keyof NameLoop810> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];
const ITEM_KEYS_810: ReadonlyArray<keyof ItemLoop810> = [
  'it1',
  'pid',
  'refs',
  'sac',
  'txi',
  'dtm',
  'other',
];

/** Builds the data-type sections for one 810 transaction, omitting any
 * section with nothing in it. */
function buildSections810(parsed: Parsed810, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_810) as HeaderKey810[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_810[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(
      parsed.summary,
      ['tds', 'sac', 'txi', 'ctt', 'other'],
      `${txPath}.summary`
    )
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_810, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    return {
      key: path,
      entries: entriesOf(item, ITEM_KEYS_810, path),
    };
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_810[key], repeating, groups}];
  });
}

type HeaderKey843 = keyof Header843;

/** Which data-type section each 843 header-level segment belongs to. */
const HEADER_SECTION_843: Record<HeaderKey843, CommonSectionKey> = {
  bqr: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  dtm: 'dates',
  itd: 'terms',
  fob: 'terms',
  msg: 'notes',
  other: 'order',
};

const SECTION_META_843: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Quote response details',
    description:
      'Quote number, purpose, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Dates on the quote response.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved — buyer, vendor, ship from/to — and where.',
  },
  terms: {
    title: 'Pricing, terms & shipping',
    description: 'Payment terms, FOB and shipping instructions.',
  },
  items: {
    title: 'Quoted line items',
    description: 'Each quoted product, quantity and quoted price.',
  },
  notes: {
    title: 'Notes',
    description: 'Free-form messages on the quote response.',
  },
  totals: {title: 'Totals', description: 'Line-item count and quote totals.'},
};

const NAME_LOOP_KEYS_843: ReadonlyArray<keyof NameLoop843> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];
const ITEM_KEYS_843: ReadonlyArray<Exclude<keyof ItemLoop843, 'subNameLoops'>> =
  ['po1', 'pid', 'mea', 'ctp', 'sac', 'refs', 'per', 'sdq', 'dtm', 'other'];
const ITEM_NAME_KEYS_843: ReadonlyArray<keyof ItemNameSubLoop843> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'other',
];

/** Builds the data-type sections for one 843 transaction, omitting any
 * section with nothing in it. */
function buildSections843(parsed: Parsed843, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_843) as HeaderKey843[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_843[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_843, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_843, path);
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_843, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_843[key], repeating, groups}];
  });
}

// --- 997 -----------------------------------------------------------------

const SECTION_META_997: Record<
  'acknowledgmentSummary' | 'transactionSetAcks',
  {title: string; description: string}
> = {
  acknowledgmentSummary: {
    title: 'Acknowledgment summary',
    description:
      'The functional group being acknowledged and its overall acceptance result.',
  },
  transactionSetAcks: {
    title: 'Transaction set acknowledgments',
    description:
      'One row per transaction set in the acknowledged group, with any segment- and element-level error notes.',
  },
};

/** Builds the data-type sections for one 997 transaction: an
 * `acknowledgmentSummary` section (AK1/AK9) and a `transactionSetAcks`
 * section with one group per AK2 loop, each nesting one `subgroups` entry
 * per AK3 segment note (itself holding that AK3's AK4 element notes).
 * Omits either section if it has nothing in it. */
function buildSections997(parsed: Parsed997, txPath: string): Section[] {
  const sections: Section[] = [];

  const summaryEntries = entriesOf(parsed, ['ak1', 'ak9'], txPath);
  if (summaryEntries.length > 0) {
    sections.push({
      key: 'acknowledgmentSummary',
      ...SECTION_META_997.acknowledgmentSummary,
      repeating: false,
      groups: [{key: txPath, entries: summaryEntries}],
    });
  }

  const ak2Groups: SectionGroup[] = parsed.ak2Loops.map((loop, i) => {
    const loopPath = `${txPath}.ak2Loops[${i}]`;
    const entries = entriesOf(loop, ['ak2', 'ak5'], loopPath);
    const subgroups: SectionGroup[] = loop.ak3Loops.map((ak3Loop, j) => {
      const ak3LoopPath = `${loopPath}.ak3Loops[${j}]`;
      const ak3Entries = entriesOf(ak3Loop, ['ak3'], ak3LoopPath);
      ak3Loop.ak4.forEach((segment, k) => {
        ak3Entries.push({segment, basePath: `${ak3LoopPath}.ak4[${k}]`});
      });
      return {key: ak3LoopPath, entries: ak3Entries};
    });
    return {key: loopPath, entries, subgroups};
  });

  if (ak2Groups.length > 0) {
    sections.push({
      key: 'transactionSetAcks',
      ...SECTION_META_997.transactionSetAcks,
      repeating: true,
      groups: ak2Groups,
    });
  }

  return sections;
}

type HeaderKey844 = keyof Header844;

/** Which data-type section each 844 header-level segment belongs to. */
const HEADER_SECTION_844: Record<HeaderKey844, CommonSectionKey> = {
  baa: 'order',
  con: 'order',
  refs: 'order',
  dtm: 'dates',
  other: 'order',
};

const SECTION_META_844: Record<
  CommonSectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Claim details',
    description:
      'Transaction purpose and type, contract reference and reference numbers.',
  },
  dates: {
    title: 'Dates',
    description: 'Dates on the claim.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved and where.',
  },
  terms: {
    title: 'Terms',
    description: 'Not used by the 844 — always empty.',
  },
  items: {
    title: 'Adjustment lines',
    description: 'Each claimed product transfer and its amounts/quantities.',
  },
  notes: {
    title: 'Notes',
    description: 'Not used by the 844 — always empty.',
  },
  totals: {
    title: 'Totals',
    description: 'Not used by the 844 — always empty.',
  },
};

const NAME_LOOP_KEYS_844: ReadonlyArray<keyof NameLoop844> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];
const ITEM_KEYS_844: ReadonlyArray<keyof ItemLoop844> = [
  'pad',
  'qty',
  'amt',
  'ctp',
  'other',
];

/** Builds the data-type sections for one 844 transaction, omitting any
 * section with nothing in it. There is no summary section — the 844 has no
 * count cross-check or transaction-level totals. */
function buildSections844(parsed: Parsed844, txPath: string): Section[] {
  const flat: Record<CommonSectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_844) as HeaderKey844[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_844[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_844, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    return {
      key: path,
      entries: entriesOf(item, ITEM_KEYS_844, path),
    };
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_844[key], repeating, groups}];
  });
}

// --- dispatcher ----------------------------------------------------------

/** Builds the data-type sections for one explained transaction — 850, 852,
 * 855, 856, 997 or 840, whichever of
 * `parsed850`/`parsed852`/`parsed855`/`parsed856`/`parsed997`/`parsed840` is
 * present. A transaction that's none of those (shown read-only) yields no
 * sections. */
export function buildSections(
  transaction: EdiTransactionExplained,
  groupIndex: number,
  transactionIndex: number
): Section[] {
  const txPath = transactionPathPrefix(groupIndex, transactionIndex);
  if (transaction.parsed850)
    return buildSections850(transaction.parsed850, txPath);
  if (transaction.parsed852)
    return buildSections852(transaction.parsed852, txPath);
  if (transaction.parsed855)
    return buildSections855(transaction.parsed855, txPath);
  if (transaction.parsed856)
    return buildSections856(transaction.parsed856, txPath);
  if (transaction.parsed997)
    return buildSections997(transaction.parsed997, txPath);
  if (transaction.parsed840)
    return buildSections840(transaction.parsed840, txPath);
  if (transaction.parsed810)
    return buildSections810(transaction.parsed810, txPath);
  if (transaction.parsed843)
    return buildSections843(transaction.parsed843, txPath);
  if (transaction.parsed844)
    return buildSections844(transaction.parsed844, txPath);
  if (transaction.parsed845)
    return buildSections845(transaction.parsed845, txPath);
  if (transaction.parsed867)
    return buildSections867(transaction.parsed867, txPath);
  if (transaction.parsed210)
    return buildSections210(transaction.parsed210, txPath);
  return [];
}
